import { CloseOutlined } from '@ant-design/icons';
import { Row, theme } from 'antd';
import _, { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCategoriesByCategoryID, selectCategoryFieldsByCategoryID } from 'reduxStore/categories/selectors';
import { categoriesActions } from 'reduxStore/categories/slice';
import { selectFieldTypes } from 'reduxStore/types/selectors';
import { Button, Col, Empty, Flex, Input, Select, Tooltip } from 'zui/Antd';

const AdditionalInformationPanelContent = ({ categoryID }) => {
	const {
		token: { marginMD, fontSize, colorTextDescription, paddingMD }
	} = theme.useToken();
	const dispatch = useDispatch();

	// Redux State
	const fieldTypes = useSelector(selectFieldTypes);
	const category = useSelector((state) => selectCategoriesByCategoryID(state, categoryID));
	const categoryFields = useSelector((state) => selectCategoryFieldsByCategoryID(state, categoryID));
	const { loadingDeleteCategoryField, loadingUpdateCategoryField } = useSelector(
		(state) => state.Categories
	);

	// Local State
	const [editingValues, setEditingValues] = useState({});
	const [existingFields, setExistingFields] = useState([]);

	// Check if category is editable based on defaultCategoryID
	// If defaultCategoryID is set, it means the category is not editable
	const isEditable = useMemo(() => !category?.defaultCategoryID, [category?.defaultCategoryID]);

	// Category configuration for additional fields
	useEffect(() => {
		const mapped =
			categoryFields?.map((field) => ({
				title: field.title,
				fieldTypeID: field.fieldTypeID,
				fieldID: field.fieldID
			})) || [];

		setExistingFields(mapped);
		setEditingValues(Object.fromEntries(mapped.map((f) => [f.fieldID, f.title])));
	}, [categoryFields]);

	const handleInputChange = (fieldID, value) => {
		setEditingValues((prev) => ({ ...prev, [fieldID]: value }));
	};

	// Update category field
	// Check if new value is different from original value
	const dispatchUpdate = useCallback(
		(newVal, fieldID, originalValue) => {
			const newValue = typeof newVal === 'string' ? newVal.trim() : newVal;

			if (!_.isEqual(newValue, originalValue) && !_.isNil(newValue) && newValue !== '') {
				dispatch(
					categoriesActions.updateCategoryField({
						categoryID,
						fieldID,
						data: { title: newValue }
					})
				);
			}
		},
		[dispatch, categoryID]
	);

	// Debounced update to prevent excessive dispatches
	const debouncedDispatchUpdate = useMemo(
		() => debounce((val, fieldID, originalValue) => dispatchUpdate(val, fieldID, originalValue), 500),
		[dispatchUpdate]
	);

	// Handle field creation
	const handleDeleteField = (fieldID) => {
		dispatch(categoriesActions.deleteCategoryField({ categoryID, fieldID }));
	};

	return (
		<Flex vertical>
			{existingFields.length === 0 ? (
				<Empty
					description='No custom fields yet'
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					style={{ margin: '16px 0' }}
				/>
			) : (
				existingFields.map((field) => {
					const { fieldID, title, fieldTypeID } = field;
					const inputValue = editingValues[fieldID] ?? title;

					return (
						<Row
							key={fieldID}
							gutter={[paddingMD, paddingMD]}
							style={{ marginBottom: marginMD }}
							wrap
							justify='space-between'
							align='middle'
						>
							<Col xs={24} md={12}>
								<Input
									value={inputValue}
									disabled={!isEditable || loadingUpdateCategoryField}
									onChange={(e) => {
										handleInputChange(fieldID, e.target.value);
										debouncedDispatchUpdate(e.target.value, fieldID, title);
									}}
								/>
							</Col>

							<Col xs={24} md={12}>
								<Flex justify='space-between' align='center'>
									<Select
										value={fieldTypeID}
										disabled={true}
										options={fieldTypes.map((type) => ({
											label: type.name,
											value: type.fieldTypeID
										}))}
										style={{ width: '100%' }}
									/>

									{isEditable && (
										<Tooltip title='Delete Field'>
											<Button
												style={{ marginLeft: paddingMD }}
												type='text'
												icon={
													<CloseOutlined
														style={{
															fontSize: fontSize,
															color: colorTextDescription
														}}
													/>
												}
												onClick={() => handleDeleteField(fieldID)}
												disabled={loadingDeleteCategoryField}
											/>
										</Tooltip>
									)}
								</Flex>
							</Col>
						</Row>
					);
				})
			)}
		</Flex>
	);
};

export default AdditionalInformationPanelContent;
